import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useJourney } from "@/context/JourneyContext";
import { StageFrame, Eyebrow, EASE } from "@/components/StageFrame";
import { ConsultantNav } from "@/components/Navigation";
import { api } from "@/lib/api";
import { ArrowRight, Mail, Loader2, CheckCircle2, Send } from "lucide-react";

const CONTACT_OPTIONS = ["Email", "Phone call", "SMS"];
const emailRe = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const LOCATION_LABELS = {
  growth: "Growth corridor", schools: "Good schools", childcare: "Childcare",
  transport: "Transport links", work: "Close to work", shops: "Shops & dining",
  parks: "Parks & nature", medical: "Medical & hospitals",
};

function BuyerFields({ index, buyer, onChange }) {
  const set = (k, v) => onChange(index, { ...buyer, [k]: v });
  const p = `profile-buyer${index + 1}`;
  return (
    <div className="rounded-2xl border border-line bg-white/5 p-5">
      <span className="label-eyebrow text-mutedText">Buyer {index + 1}</span>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input data-testid={`${p}-name`} value={buyer.name} onChange={(e) => set("name", e.target.value)}
          placeholder="Full name"
          className="rounded-xl border border-line bg-white/5 px-4 py-3 text-forest placeholder:text-mutedText/60 focus:outline-none focus:ring-2 focus:ring-brand" />
        <input data-testid={`${p}-mobile`} value={buyer.mobile} onChange={(e) => set("mobile", e.target.value)}
          placeholder="Mobile" inputMode="tel"
          className="rounded-xl border border-line bg-white/5 px-4 py-3 text-forest placeholder:text-mutedText/60 focus:outline-none focus:ring-2 focus:ring-brand" />
        <input data-testid={`${p}-email`} value={buyer.email} onChange={(e) => set("email", e.target.value)}
          placeholder="Email" inputMode="email"
          className="rounded-xl border border-line bg-white/5 px-4 py-3 text-forest placeholder:text-mutedText/60 focus:outline-none focus:ring-2 focus:ring-brand" />
        <select data-testid={`${p}-contact`} value={buyer.contact} onChange={(e) => set("contact", e.target.value)}
          className="rounded-xl border border-line bg-surface px-4 py-3 text-forest focus:outline-none focus:ring-2 focus:ring-brand">
          {CONTACT_OPTIONS.map((o) => <option key={o} value={o}>{`Prefers ${o}`}</option>)}
        </select>
      </div>
    </div>
  );
}

export default function Closing() {
  const { session, data, back } = useJourney();
  const navigate = useNavigate();
  const name = session?.client_name || "there";

  const existing = data.profile?.buyers;
  const [buyers, setBuyers] = useState(
    existing && existing.length === 2
      ? existing
      : [
          { name: name !== "there" ? name : "", mobile: "", email: "", contact: "Email" },
          { name: "", mobile: "", email: "", contact: "Email" },
        ]
  );
  const [postcode, setPostcode] = useState(data.profile?.postcode || "");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(!!data.journey_sent);
  const [voucher, setVoucher] = useState(data.voucher || null);

  // Derived data for the emailed keepsake.
  const selected = data.selected_home || {};
  const chosen = (data.matches || {})[selected.kind] || {};

  const updateBuyer = (i, next) => setBuyers((b) => b.map((x, idx) => (idx === i ? next : x)));

  const validEmails = buyers.filter((b) => emailRe.test((b.email || "").trim()));

  const saveAndSend = async () => {
    if (validEmails.length === 0) {
      toast.error("Add at least one valid buyer email to send the journey.");
      return;
    }
    setSending(true);
    try {
      const profile = { buyers, postcode };
      await api.patchSession(session.id, { data: { profile } });
      const res = await api.sendJourney(session.id);
      setSent(true);
      if (res.voucher) setVoucher(res.voucher);
      toast.success(`Journey sent to ${res.recipients.length} ${res.recipients.length === 1 ? "buyer" : "buyers"}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the journey.");
    } finally {
      setSending(false);
    }
  };

  return (
    <StageFrame testId="stage-closing" className="px-6 md:px-14 pt-16 pb-28">
      <div className="max-w-3xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE }} className="text-center">
          <Eyebrow className="justify-center">Your journey, captured</Eyebrow>
          <h1 className="font-serif text-4xl md:text-6xl font-light tracking-tight text-forest mt-4 leading-[1.02]">
            This is the start<br />of your home, {name}.
          </h1>
          <p className="text-mutedText mt-5 max-w-xl mx-auto">
            {data.life_profile?.lifestyle_statement || "You've discovered how you want to live, what's possible, and the home that fits."} Let's send your full plan so you can revisit it any time.
          </p>
        </motion.div>

        {/* Full profile capture */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE, delay: 0.15 }}
          className="mt-10 rounded-3xl border border-line bg-white/[0.03] p-6 md:p-8" data-testid="profile-capture">
          <div className="flex items-center gap-2">
            <Mail size={18} className="text-brand" />
            <h2 className="font-serif text-2xl text-forest">Where should we send your journey?</h2>
          </div>
          <p className="text-mutedText text-sm mt-1.5">
            We'll email a branded summary — your chosen home &amp; floor plan, budget and top location priorities — to both of you.
          </p>

          <div className="mt-6 space-y-4">
            {buyers.map((b, i) => <BuyerFields key={i} index={i} buyer={b} onChange={updateBuyer} />)}
            <div className="max-w-[220px]">
              <label className="label-eyebrow text-mutedText">Postcode</label>
              <input data-testid="profile-postcode" value={postcode} onChange={(e) => setPostcode(e.target.value)}
                placeholder="e.g. 2000" inputMode="numeric"
                className="mt-1.5 w-full rounded-xl border border-line bg-white/5 px-4 py-3 text-forest placeholder:text-mutedText/60 focus:outline-none focus:ring-2 focus:ring-brand" />
            </div>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-4">
            <button data-testid="profile-send-btn" onClick={saveAndSend} disabled={sending}
              className="inline-flex items-center gap-2 rounded-full bg-brand px-7 py-3.5 text-white text-sm font-medium shadow-xl shadow-brand/25 transition hover:bg-brand-dark disabled:opacity-50">
              {sending ? <><Loader2 size={16} className="animate-spin" /> Sending…</>
                : sent ? <><Send size={16} /> Resend our journey</>
                : <><Send size={16} /> Save &amp; email our journey</>}
            </button>
            {sent && (
              <span data-testid="profile-sent-status" className="inline-flex items-center gap-2 text-sm text-emerald-300">
                <CheckCircle2 size={16} /> Sent to {validEmails.length} {validEmails.length === 1 ? "inbox" : "inboxes"}
              </span>
            )}
          </div>
          {sent && voucher?.code && (
            <div data-testid="profile-voucher" className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border-2 border-dashed border-brand/50 bg-brand/5 px-5 py-4">
              <div>
                <span className="label-eyebrow text-brand">Free Upgrades Voucher</span>
                <p className="text-sm text-forest/80 mt-1">${(voucher.total || 0).toLocaleString()} towards inclusions · redeemable at your Floorplan &amp; Inclusions appointment{voucher.expires ? ` · valid until ${voucher.expires}` : ""}</p>
              </div>
              <span className="font-mono text-lg font-semibold tracking-widest text-brand">{voucher.code}</span>
            </div>
          )}
          <p className="text-mutedText/60 text-xs mt-3">Sent from PropIntellix · replies go to Sales@propintellix.com.au</p>
        </motion.div>

        <div className="mt-12 text-center">
          <button data-testid="closing-partii-btn"
            className="group inline-flex items-center gap-3 rounded-full border border-line bg-white/5 px-8 py-3.5 text-forest font-medium transition hover:bg-white/10">
            Begin Part II — Personalise your home
            <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
          </button>
          <div className="mt-6">
            <button data-testid="closing-restart-btn" onClick={() => navigate(0)}
              className="hotspot text-sm text-mutedText hover:text-forest underline underline-offset-4">
              Start a new session
            </button>
          </div>
        </div>
      </div>

      <ConsultantNav testId="stage-closing" onBack={back} onNext={null} />
    </StageFrame>
  );
}
